// Ist eine Datei, was ihre Art sagt? Ein Blick auf den Anfang genügt: ein
// JPEG ohne Metadaten mit lesbarem Bildkopf (foto-metadaten.ts), ein PDF
// („%PDF-“), ein Ton im Format MP4 („ftyp“ ab Byte 4 – das iPhone) oder
// WebM (EBML – andere Browser). Mehr prüft niemand; geöffnet wird die Datei
// nicht.
//
// Seit E3c (S3, Prüfung des Plans, Befund 11) der Excel-Report von K2: eine
// .xlsx ist ein ZIP-Archiv – sie beginnt mit „PK“, und im
// Inhaltsverzeichnis am Ende steht mindestens ein Eintrag unter „xl/“ (die
// Arbeitsmappe). Dafür braucht es die ganze Datei (höchstens 10 MB);
// entpackt wird nichts.
//
// An EINER Stelle für App und Server (Code-Prüfung E2, Befund 8,
// 22.10.2026): Die Doku-App prüft das K2-PDF schon beim Ablegen (k2.ts),
// der Server beim Hochladen und noch einmal am Eintrag
// (server/src/doku/dateien.ts, eintraege.ts). So weist die App nie ab,
// was der Server annähme – und umgekehrt.

import { XLSX, type DateiArt } from "./aufnahme-vertrag.ts";
import { jpegMasse, jpegPruefen } from "./foto-metadaten.ts";

/** So viel vom Anfang einer Datei genügt, um Ton und PDF zu erkennen. */
export const ANFANG_ZUM_ERKENNEN = 16;

const PDF_ANFANG = [0x25, 0x50, 0x44, 0x46, 0x2d]; // „%PDF-“
const MP4_FTYP = [0x66, 0x74, 0x79, 0x70]; // „ftyp“ ab Byte 4
const WEBM_ANFANG = [0x1a, 0x45, 0xdf, 0xa3]; // EBML

function beginntMit(daten: Uint8Array, ab: number, erwartet: readonly number[]): boolean {
  return erwartet.every((b, i) => daten[ab + i] === b);
}

const ZIP_ANFANG = [0x50, 0x4b, 0x03, 0x04]; // „PK“ – der erste Eintrag
const ZIP_VERZEICHNIS_ENDE = [0x50, 0x4b, 0x05, 0x06]; // „PK“ – das Ende des Inhaltsverzeichnisses
const ZIP_VERZEICHNIS_EINTRAG = [0x50, 0x4b, 0x01, 0x02]; // „PK“ – ein Eintrag im Inhaltsverzeichnis

/**
 * Die Namen im Inhaltsverzeichnis eines ZIP-Archivs – null, wenn es keines
 * ist oder das Verzeichnis nicht stimmt. Gelesen wird nur das Verzeichnis
 * am Ende (es nennt jede Datei des Archivs); ZIP64 kommt bei 10 MB nicht vor
 * und zählt als „kein Archiv“.
 */
export function zipNamen(daten: Uint8Array): string[] | null {
  if (daten.length < 22 || !beginntMit(daten, 0, ZIP_ANFANG)) return null;
  const sicht = new DataView(daten.buffer, daten.byteOffset, daten.byteLength);
  // Das Ende des Verzeichnisses: 22 Bytes, danach höchstens ein Kommentar von 65 535 Bytes.
  let ende = -1;
  for (let i = daten.length - 22; i >= Math.max(0, daten.length - 22 - 0xffff); i -= 1) {
    if (beginntMit(daten, i, ZIP_VERZEICHNIS_ENDE)) {
      ende = i;
      break;
    }
  }
  if (ende < 0) return null;
  const anzahl = sicht.getUint16(ende + 10, true);
  const groesse = sicht.getUint32(ende + 12, true);
  const anfang = sicht.getUint32(ende + 16, true);
  if (anfang + groesse > ende) return null;
  const namen: string[] = [];
  let p = anfang;
  const leser = new TextDecoder("utf-8", { fatal: false });
  for (let n = 0; n < anzahl; n += 1) {
    if (p + 46 > ende || !beginntMit(daten, p, ZIP_VERZEICHNIS_EINTRAG)) return null;
    const nameLaenge = sicht.getUint16(p + 28, true);
    const extraLaenge = sicht.getUint16(p + 30, true);
    const kommentarLaenge = sicht.getUint16(p + 32, true);
    if (p + 46 + nameLaenge > ende) return null;
    namen.push(leser.decode(daten.subarray(p + 46, p + 46 + nameLaenge)));
    p += 46 + nameLaenge + extraLaenge + kommentarLaenge;
  }
  return namen;
}

/** Eine Arbeitsmappe von Excel (.xlsx): ein ZIP-Archiv mit Einträgen unter „xl/“. */
function istXlsx(daten: Uint8Array): boolean {
  const namen = zipNamen(daten);
  return namen !== null && namen.some((n) => n.startsWith("xl/"));
}

/**
 * Ist der Inhalt, was die Art sagt? Null: ja. Sonst die Meldung für den
 * Menschen (ohne Inhalte). Für Fotos braucht es die ganze Datei (Metadaten
 * und Bildkopf), ebenso für den Excel-Report (das Inhaltsverzeichnis steht
 * am Ende); für Ton und PDF reicht `ANFANG_ZUM_ERKENNEN`.
 */
export function dateiInhaltPruefen(art: DateiArt, daten: Uint8Array): string | null {
  switch (art) {
    case "image/jpeg": {
      const p = jpegPruefen(daten);
      if (!p.ok && p.grund === "metadaten") {
        return `Das Foto enthält noch Metadaten (${p.gefunden.join(", ")}) – der Server nimmt nur Fotos ohne an.`;
      }
      if (!p.ok || jpegMasse(daten) === null) return "Das ist kein lesbares Foto (JPEG).";
      return null;
    }
    case "application/pdf":
      return beginntMit(daten, 0, PDF_ANFANG) ? null : "Das ist kein PDF.";
    case "audio/mp4":
      return beginntMit(daten, 4, MP4_FTYP) ? null : "Das ist keine Tonaufnahme im Format MP4.";
    case "audio/webm":
      return beginntMit(daten, 0, WEBM_ANFANG) ? null : "Das ist keine Tonaufnahme im Format WebM.";
    case XLSX:
      return istXlsx(daten) ? null : "Das ist keine Excel-Datei (.xlsx).";
  }
}
